import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, mutate, uploadFile, downloadFile } from '../shared/api';
import { DateInput } from '../shared/DateInput';
import { formatDate } from '../shared/formatting';
import { Page, PageHeader, Card, Btn, Select, Pill, DataTable, ui, type Col } from '../shared/ui';

type Locale = 'hr' | 'en';
type Tab = 'dashboard' | 'saldo' | 'polog' | 'sravnavanje';

type SourceStat = { rows: number; min_date: string | null; max_date: string | null } | null;
type StateResp = { ism: SourceStat; ism_opening?: SourceStat; besoft: SourceStat; faros: SourceStat };

type IngestResult = { batch_id: number; rows_inserted: number; rows_skipped: number };
type IngestResp = { ism: IngestResult | null; ism_opening?: IngestResult | null; besoft: IngestResult | null; faros: IngestResult | null };

type RunSummary = { line_count: number; by_status: Record<string, number>; by_report: Record<string, number> };
type RunRow = {
    id: number;
    run_type: string;
    date_from: string;
    date_to: string;
    status: string;
    summary: RunSummary | null;
    error: string | null;
    created_at: string | null;
};
type RunsResp = { runs: RunRow[] };

type LineRow = {
    id: number;
    report_type: string;
    branch_code: string | null;
    branch_name: string | null;
    entry_date: string | null;
    payment_amount_eur: string | number;
    cash_amount_eur: string | number;
    ism_amount_eur: string | number;
    besoft_amount_eur: string | number;
    difference_eur: string | number;
    request_count: number;
    received_without_deposit_count: number;
    status: string;
    notes: string | null;
};
type LinesResp = { run: RunRow; lines: LineRow[] };

type PologRow = {
    id: string;
    branch_code: string;
    branch_name: string;
    has_exchange: boolean;
    opening_eur: number | null;
    morning_deposit_eur: number | null;
    morning_source?: string | null;
    buyback_eur: number | null;
    residual_eur: number | null;
    faros_saldo_eur: number | null;
    ism_opening_total_eur: number | null;
    cash_max_eur: number;
    excess_eur: number;
    status: string;
    source: string;
};
type PologResp = {
    date: string;
    legal: { adms_company_eur: number; exchange_branch_eur: number; gold_only_branch_eur: number };
    rows: PologRow[];
    totals: {
        branch_count: number;
        gold_only_residual_eur: number;
        gold_only_over_company_max: number;
        exchange_ism_total_eur: number;
        excess_eur: number;
        polog_branch_count: number;
    };
    missing: string[];
};

type LiveRow = {
    id: string;
    branch_no: string;
    branch_code: string;
    branch_name: string;
    uplata: number;
    isplata: number;
    saldo: number;
};
type LiveResp = {
    date: string;
    rows: LiveRow[];
    totals: { uplata: number; isplata: number; saldo: number };
};

type BranchOption = { code: string; name: string };
type BranchesResp = { branches: BranchOption[] };

type SaldoRow = {
    id: string;
    sifra: string;
    branch_code: string;
    branch_name: string;
    opening_eur: number;
    uplata_eur: number;
    uplata_source?: string | null;
    isplata_eur: number;
    closing_eur: number;
    cash_max_eur: number;
    transfer_eur: number;
};
type SaldoResp = {
    date_from: string;
    date_to: string;
    legal: { adms_company_eur: number; exchange_branch_eur: number; gold_only_branch_eur: number };
    rows: SaldoRow[];
    totals: { opening_eur: number; uplata_eur: number; isplata_eur: number; closing_eur: number; transfer_eur: number; branch_count: number };
    missing: string[];
};

const T = {
    hr: {
        title: 'Sravnavanje blagajni',
        subtitle: 'Uplate i isplate, polog utrška, sravnavanje s priljevom',
        tabLive: 'Uplate i isplate',
        tabSaldo: 'Saldo blagajne',
        tabPolog: 'Polog utrška',
        tabSrav: 'Sravnavanje A/B/C',
        openDash: 'Dashboard',
        ingest: 'Uvoz izvora',
        ism: 'ISM dnevnik (xlsx)',
        ismOpening: 'ISM početna stanja (pdf)',
        besoft: 'Besoft (zalog)',
        faros: 'Faros saldo (ERP)',
        upload: 'Uvezi',
        run: 'Pokreni sravnavanje',
        from: 'Od',
        to: 'Do',
        type: 'Vrsta',
        tolerance: 'Tolerancija (EUR)',
        start: 'Pokreni',
        runs: 'Prošla sravnavanja',
        rows: 'redaka',
        noData: 'Nema podataka. Uvezi Faros saldo i, za poslovnice s mjenjačnicom, ISM PDF.',
        back: 'Natrag',
        lines: 'Linije runa',
        export: 'Izvezi XLSX',
        report: 'Izvještaj',
        branch: 'Poslovnica',
        date: 'Datum',
        payout: 'Isplata',
        cash: 'Blagajna',
        diff: 'Razlika',
        status: 'Status',
        note: 'Napomena',
        pickFile: 'Odaberi fajl',
        pickBranch: 'Odaberi poslovnicu i upiši iznos',
        chooseBranch: '- odaberi -',
        opening: 'Početno stanje',
        morning: 'Uplata ujutro',
        buyback: 'Otkup',
        residual: 'Residual',
        max: 'Blag. max',
        excess: 'Višak / polog',
        exchange: 'Mjenjačnica',
        load: 'Učitaj',
        missing: 'Nedostaje dnevni promet Faros (AD_STANJE_BLG_TAB) — otkup prazan. Uplata ujutro iz m.21 Dnevna gotovina (Izvršeno/Završeno) ako postoji.',
        liveBranch: 'Broj poslovnice',
        liveIn: 'Uplata',
        liveOut: 'Isplata',
        liveSaldo: 'Saldo',
        saldoOpening: 'Početno stanje',
        saldoIn: 'Uplate',
        saldoOut: 'Isplate (trž.nakupi)',
        saldoClosing: 'Završno stanje',
        saldoMax: 'Blagajnički maksimum',
        saldoTransfer: 'Prijenos / polog gotovine',
        add: 'Dodaj',
        kind: 'Vrsta',
        amount: 'Iznos (EUR)',
    },
    en: {
        title: 'Cash reconciliation',
        subtitle: 'Deposits and withdrawals, cash deposit control, payout vs inflow',
        tabLive: 'Deposits & withdrawals',
        tabSaldo: 'Cash register balance',
        tabPolog: 'Cash deposit',
        tabSrav: 'Reconciliation A/B/C',
        openDash: 'Dashboard',
        ingest: 'Import sources',
        ism: 'ISM journal (xlsx)',
        ismOpening: 'ISM opening (pdf)',
        besoft: 'Besoft (pawn)',
        faros: 'Faros balance (ERP)',
        upload: 'Import',
        run: 'Run reconciliation',
        from: 'From',
        to: 'To',
        type: 'Type',
        tolerance: 'Tolerance (EUR)',
        start: 'Run',
        runs: 'Past runs',
        rows: 'rows',
        noData: 'No data. Import Faros balance and, for exchange branches, the ISM PDF.',
        back: 'Back',
        lines: 'Run lines',
        export: 'Export XLSX',
        report: 'Report',
        branch: 'Branch',
        date: 'Date',
        payout: 'Payout',
        cash: 'Cash',
        diff: 'Difference',
        status: 'Status',
        note: 'Note',
        pickFile: 'Pick file',
        pickBranch: 'Pick a branch and enter amount',
        chooseBranch: '- pick -',
        opening: 'Opening',
        morning: 'Morning deposit',
        buyback: 'Buyback',
        residual: 'Residual',
        max: 'Cash max',
        excess: 'Excess / deposit',
        exchange: 'Exchange',
        load: 'Load',
        missing: 'Daily Faros ledger missing (AD_STANJE_BLG_TAB) — buyback empty. Morning deposit comes from m.21 Daily cash (Executed/Done) when present.',
        liveBranch: 'Branch no.',
        liveIn: 'Deposit',
        liveOut: 'Withdrawal',
        liveSaldo: 'Balance',
        saldoOpening: 'Opening',
        saldoIn: 'Deposits',
        saldoOut: 'Withdrawals (market purchases)',
        saldoClosing: 'Closing',
        saldoMax: 'Cash-register maximum',
        saldoTransfer: 'Transfer / cash deposit',
        add: 'Add',
        kind: 'Type',
        amount: 'Amount (EUR)',
    },
} as const;

const eur = (v: string | number | null | undefined): string => {
    if (v === null || v === undefined || v === '') return '';
    const n = typeof v === 'string' ? parseFloat(v) : v;
    if (!Number.isFinite(n)) return '';
    return n.toLocaleString('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const statusTone = (s: string): 'ok' | 'warn' | 'bad' | 'info' | 'neutral' => {
    if (s === 'usklađeno' || s === 'ok' || s === 'mjenjacnica') return 'ok';
    if (s === 'manjak' || s === 'potvrda_bez_pokrica' || s === 'polog' || s === 'faros_nije_nula') return 'bad';
    if (s === 'visak' || s === 'neevidentirano') return 'warn';
    if (s === 'izvor_nedostaje') return 'info';
    return 'neutral';
};

const today = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const firstOfMonth = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

export default function ReconciliationModule({ token, locale, onBack }: { token: string | null; locale: Locale; onBack: () => void }) {
    const t = T[locale];
    const [tab, setTab] = useState<Tab>('dashboard');
    const [state, setState] = useState<StateResp | null>(null);
    const [runs, setRuns] = useState<RunRow[] | null>(null);
    const [selected, setSelected] = useState<LinesResp | null>(null);
    const [polog, setPolog] = useState<PologResp | null>(null);
    const [saldo, setSaldo] = useState<SaldoResp | null>(null);
    const [live, setLive] = useState<LiveResp | null>(null);
    const [branches, setBranches] = useState<BranchOption[]>([]);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);

    const [pologDate, setPologDate] = useState(today());
    const [liveDate, setLiveDate] = useState(today());
    const [liveBranch, setLiveBranch] = useState('');
    const [liveKind, setLiveKind] = useState('uplata');
    const [liveAmount, setLiveAmount] = useState('');
    const [dateFrom, setDateFrom] = useState(firstOfMonth());
    const [dateTo, setDateTo] = useState(today());
    const [runType, setRunType] = useState('full');
    const [tolerance, setTolerance] = useState('1');

    const ismRef = useRef<HTMLInputElement>(null);
    const ismOpeningRef = useRef<HTMLInputElement>(null);
    const besoftRef = useRef<HTMLInputElement>(null);
    const farosRef = useRef<HTMLInputElement>(null);

    const loadState = useCallback(async () => {
        try {
            const s = await api<StateResp>('/payment-traffic/reconciliation/state', token);
            setState(s);
        } catch (e) {
            setMsg((e as Error).message);
        }
    }, [token]);

    const loadRuns = useCallback(async () => {
        try {
            const r = await api<RunsResp>('/payment-traffic/reconciliation/runs', token);
            setRuns(r.runs);
        } catch (e) {
            setMsg((e as Error).message);
        }
    }, [token]);

    const loadPolog = useCallback(async (iso: string) => {
        setBusy(true);
        try {
            const r = await api<PologResp>(`/payment-traffic/reconciliation/polog-utrska?date=${iso}`, token);
            setPolog(r);
        } catch (e) {
            setMsg((e as Error).message);
        } finally {
            setBusy(false);
        }
    }, [token]);

    const loadSaldo = useCallback(async (from: string, to: string) => {
        setBusy(true);
        try {
            const r = await api<SaldoResp>(`/payment-traffic/reconciliation/saldo-blagajne?date_from=${from}&date_to=${to}`, token);
            setSaldo(r);
        } catch (e) {
            setMsg((e as Error).message);
        } finally {
            setBusy(false);
        }
    }, [token]);

    const loadLive = useCallback(async (iso: string) => {
        try {
            const r = await api<LiveResp>(`/payment-traffic/reconciliation/uplate-isplate?date=${iso}`, token);
            setLive(r);
        } catch (e) {
            setMsg((e as Error).message);
        }
    }, [token]);

    const loadBranches = useCallback(async () => {
        try {
            const r = await api<BranchesResp>('/payment-traffic/reconciliation/branches', token);
            setBranches(r.branches);
        } catch (e) {
            setMsg((e as Error).message);
        }
    }, [token]);

    useEffect(() => {
        void loadState();
        void loadRuns();
        void loadPolog(pologDate);
        void loadSaldo(dateFrom, dateTo);
        void loadLive(liveDate);
    }, [loadState, loadRuns, loadPolog, loadSaldo, loadLive, pologDate, dateFrom, dateTo, liveDate]);

    useEffect(() => {
        void loadBranches();
    }, [loadBranches]);

    useEffect(() => {
        if (tab !== 'dashboard') {
            return;
        }
        const id = window.setInterval(() => { void loadLive(liveDate); }, 5000);
        return () => window.clearInterval(id);
    }, [tab, liveDate, loadLive]);

    const doAddLive = useCallback(async () => {
        const amt = parseFloat(liveAmount.replace(',', '.'));
        if (!liveBranch.trim() || !Number.isFinite(amt) || amt <= 0) {
            setMsg(t.pickBranch);
            return;
        }
        setBusy(true);
        setMsg(null);
        try {
            const r = await mutate<LiveResp>('/payment-traffic/reconciliation/uplate-isplate', token, 'POST', {
                branch_code: liveBranch.trim(),
                kind: liveKind,
                amount_eur: amt,
                date: liveDate,
            });
            setLive(r);
            setLiveAmount('');
        } catch (e) {
            setMsg((e as Error).message);
        } finally {
            setBusy(false);
        }
    }, [token, liveBranch, liveKind, liveAmount, liveDate, t]);

    const doIngest = useCallback(async () => {
        const fd = new FormData();
        let any = false;
        if (ismRef.current?.files?.[0]) { fd.append('ism', ismRef.current.files[0]); any = true; }
        if (ismOpeningRef.current?.files?.[0]) { fd.append('ism_opening', ismOpeningRef.current.files[0]); any = true; }
        if (besoftRef.current?.files?.[0]) { fd.append('besoft', besoftRef.current.files[0]); any = true; }
        if (farosRef.current?.files?.[0]) { fd.append('faros', farosRef.current.files[0]); any = true; }
        if (!any) { setMsg(t.pickFile); return; }
        setBusy(true);
        setMsg(null);
        try {
            const r = await uploadFile<IngestResp>('/payment-traffic/reconciliation/ingest', token, fd);
            const parts: string[] = [];
            if (r.ism) parts.push(`ISM +${r.ism.rows_inserted}/${r.ism.rows_skipped}`);
            if (r.ism_opening) parts.push(`ISM početna +${r.ism_opening.rows_inserted}/${r.ism_opening.rows_skipped}`);
            if (r.besoft) parts.push(`Besoft +${r.besoft.rows_inserted}/${r.besoft.rows_skipped}`);
            if (r.faros) parts.push(`Faros +${r.faros.rows_inserted}/${r.faros.rows_skipped}`);
            setMsg(parts.join(' · '));
            if (ismRef.current) ismRef.current.value = '';
            if (ismOpeningRef.current) ismOpeningRef.current.value = '';
            if (besoftRef.current) besoftRef.current.value = '';
            if (farosRef.current) farosRef.current.value = '';
            await loadState();
            await loadPolog(pologDate);
            await loadLive(liveDate);
        } catch (e) {
            setMsg((e as Error).message);
        } finally {
            setBusy(false);
        }
    }, [token, t, loadState, loadPolog, loadLive, pologDate, liveDate]);

    const doRun = useCallback(async () => {
        setBusy(true);
        setMsg(null);
        try {
            await mutate('/payment-traffic/reconciliation/run', token, 'POST', {
                date_from: dateFrom,
                date_to: dateTo,
                run_type: runType,
                tolerance_eur: parseFloat(tolerance) || 0,
            });
            await loadRuns();
        } catch (e) {
            setMsg((e as Error).message);
        } finally {
            setBusy(false);
        }
    }, [token, dateFrom, dateTo, runType, tolerance, loadRuns]);

    const openRun = useCallback(async (id: number) => {
        setBusy(true);
        try {
            const r = await api<LinesResp>(`/payment-traffic/reconciliation/runs/${id}`, token);
            setSelected(r);
        } catch (e) {
            setMsg((e as Error).message);
        } finally {
            setBusy(false);
        }
    }, [token]);

    const doExport = useCallback(async (id: number) => {
        try {
            await downloadFile(`/payment-traffic/reconciliation/export?run_id=${id}`, token, `sravnavanje_run_${id}.xlsx`);
        } catch (e) {
            setMsg((e as Error).message);
        }
    }, [token]);

    const doExportPolog = useCallback(async () => {
        try {
            await downloadFile(`/payment-traffic/reconciliation/polog-utrska/export?date=${pologDate}`, token, `polog_utrska_${pologDate}.xlsx`);
        } catch (e) {
            setMsg((e as Error).message);
        }
    }, [token, pologDate]);

    const doExportSaldo = useCallback(async () => {
        try {
            await downloadFile(`/payment-traffic/reconciliation/saldo-blagajne/export?date_from=${dateFrom}&date_to=${dateTo}`, token, `saldo_blagajne_${dateFrom}_${dateTo}.xlsx`);
        } catch (e) {
            setMsg((e as Error).message);
        }
    }, [token, dateFrom, dateTo]);

    const srcStat = (s: SourceStat): string => {
        if (!s || s.rows === 0) return '—';
        const a = s.min_date ? formatDate(s.min_date, locale) : '';
        const b = s.max_date ? formatDate(s.max_date, locale) : '';
        return `${s.rows} ${t.rows} · ${a} → ${b}`;
    };

    const runCols: Col<RunRow>[] = [
        { key: 'id', label: '#', width: 60 },
        { key: 'run_type', label: t.type, render: (r) => <Pill tone="neutral">{r.run_type}</Pill> },
        { key: 'period', label: `${t.from} → ${t.to}`, render: (r) => `${formatDate(r.date_from, locale)} → ${formatDate(r.date_to, locale)}` },
        { key: 'status', label: t.status, render: (r) => <Pill tone={r.status === 'completed' ? 'ok' : r.status === 'failed' ? 'bad' : 'warn'}>{r.status}</Pill> },
        { key: 'lines', label: t.lines, align: 'right', render: (r) => r.summary?.line_count ?? 0 },
        { key: 'created_at', label: 'Kreirano', render: (r) => (r.created_at ?? '').slice(0, 16).replace('T', ' ') },
        { key: 'act', label: '', align: 'right', render: (r) => <Btn size="sm" variant="soft" onClick={() => doExport(r.id)}>{t.export}</Btn> },
    ];

    const lineCols: Col<LineRow>[] = [
        { key: 'report_type', label: t.report, render: (r) => <Pill tone="neutral">{r.report_type}</Pill> },
        { key: 'branch', label: t.branch, render: (r) => `${r.branch_code ?? ''} ${r.branch_name ?? ''}`.trim() || '—' },
        { key: 'entry_date', label: t.date, render: (r) => (r.entry_date ? formatDate(r.entry_date, locale) : '—') },
        { key: 'payment_amount_eur', label: t.payout, align: 'right', render: (r) => eur(r.payment_amount_eur) },
        { key: 'cash_amount_eur', label: t.cash, align: 'right', render: (r) => eur(r.cash_amount_eur) },
        { key: 'ism_amount_eur', label: 'ISM', align: 'right', render: (r) => eur(r.ism_amount_eur) },
        { key: 'besoft_amount_eur', label: 'Besoft', align: 'right', render: (r) => eur(r.besoft_amount_eur) },
        { key: 'difference_eur', label: t.diff, align: 'right', render: (r) => eur(r.difference_eur) },
        { key: 'status', label: t.status, render: (r) => <Pill tone={statusTone(r.status)}>{r.status}</Pill> },
        { key: 'notes', label: t.note, render: (r) => r.notes ?? '' },
    ];

    const pologCols: Col<PologRow>[] = [
        { key: 'branch_code', label: 'Šifra', width: 70 },
        { key: 'branch_name', label: t.branch },
        { key: 'has_exchange', label: t.exchange, render: (r) => r.has_exchange ? (locale === 'hr' ? 'da' : 'yes') : (locale === 'hr' ? 'ne' : 'no') },
        { key: 'opening_eur', label: t.opening, align: 'right', render: (r) => eur(r.opening_eur) },
        { key: 'morning_deposit_eur', label: t.morning, align: 'right', render: (r) => eur(r.morning_deposit_eur) },
        { key: 'buyback_eur', label: t.buyback, align: 'right', render: (r) => eur(r.buyback_eur) },
        { key: 'residual_eur', label: t.residual, align: 'right', render: (r) => eur(r.residual_eur) },
        { key: 'cash_max_eur', label: t.max, align: 'right', render: (r) => eur(r.cash_max_eur) },
        { key: 'excess_eur', label: t.excess, align: 'right', render: (r) => eur(r.excess_eur) },
        { key: 'status', label: t.status, render: (r) => <Pill tone={statusTone(r.status)}>{r.status}</Pill> },
    ];

    const saldoCols: Col<SaldoRow>[] = [
        { key: 'sifra', label: 'Šifra', width: 70 },
        { key: 'branch_name', label: t.branch },
        { key: 'opening_eur', label: t.saldoOpening, align: 'right', render: (r) => eur(r.opening_eur) },
        { key: 'uplata_eur', label: t.saldoIn, align: 'right', render: (r) => eur(r.uplata_eur) },
        { key: 'isplata_eur', label: t.saldoOut, align: 'right', render: (r) => eur(r.isplata_eur) },
        { key: 'closing_eur', label: t.saldoClosing, align: 'right', render: (r) => eur(r.closing_eur) },
        { key: 'cash_max_eur', label: t.saldoMax, align: 'right', render: (r) => eur(r.cash_max_eur) },
        { key: 'transfer_eur', label: t.saldoTransfer, align: 'right', render: (r) => eur(r.transfer_eur) },
    ];

    const fileRow = (label: string, ref: React.RefObject<HTMLInputElement | null>, stat: SourceStat, accept: string) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: `1px solid ${ui.line2}`, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 650, color: ui.ink, minWidth: 200 }}>{label}</div>
            <input ref={ref} type="file" accept={accept} style={{ fontSize: '0.82rem' }} />
            <div style={{ fontSize: '0.78rem', color: ui.muted }}>{srcStat(stat)}</div>
        </div>
    );

    if (selected) {
        return (
            <Page>
                <PageHeader
                    icon="⚖️"
                    title={`${t.lines} #${selected.run.id}`}
                    subtitle={`${selected.run.run_type} · ${formatDate(selected.run.date_from, locale)} → ${formatDate(selected.run.date_to, locale)}`}
                    actions={(
                        <>
                            <Btn variant="soft" onClick={() => doExport(selected.run.id)}>{t.export}</Btn>
                            <Btn onClick={() => setSelected(null)}>{t.back}</Btn>
                        </>
                    )}
                />
                <Card pad={4}>
                    <DataTable cols={lineCols} rows={selected.lines} empty={t.noData} />
                </Card>
            </Page>
        );
    }

    if (tab === 'dashboard') {
        const dashBg = '#101218';
        const dashBar = '#161a22';
        const dashLine = '#2a3140';
        const dashInk = '#f4f1ea';
        const dashDim = '#8b93a3';
        const field: React.CSSProperties = { height: 36, padding: '0 10px', borderRadius: 8, border: `1px solid ${dashLine}`, background: '#0c0e14', color: dashInk };
        return (
            <div data-view="uplate-isplate-fullscreen" style={{ position: 'fixed', inset: 0, zIndex: 4000, background: dashBg, color: dashInk, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ background: dashBar, borderBottom: `1px solid ${dashLine}`, padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 800, fontSize: '1.15rem', letterSpacing: '0.04em', marginRight: 8 }}>{t.tabLive}</div>
                    <label style={{ fontSize: '0.72rem', color: dashDim }}>{t.date}
                        <div style={{ marginTop: 4, minWidth: 150 }}><DateInput value={liveDate} onChange={setLiveDate} /></div>
                    </label>
                    <label style={{ fontSize: '0.72rem', color: dashDim }}>{t.liveBranch}<br />
                        <select value={liveBranch} onChange={(e) => setLiveBranch(e.target.value)} style={{ ...field, width: 220, marginTop: 4 }}>
                            <option value="">{t.chooseBranch}</option>
                            {branches.map((b) => (
                                <option key={b.code} value={b.code}>{b.code} - {b.name}</option>
                            ))}
                        </select>
                    </label>
                    <label style={{ fontSize: '0.72rem', color: dashDim }}>{t.kind}<br />
                        <select value={liveKind} onChange={(e) => setLiveKind(e.target.value)} style={{ ...field, width: 130, marginTop: 4 }}>
                            <option value="uplata">{t.liveIn}</option>
                            <option value="isplata">{t.liveOut}</option>
                        </select>
                    </label>
                    <label style={{ fontSize: '0.72rem', color: dashDim }}>{t.amount}<br />
                        <input inputMode="decimal" value={liveAmount} onChange={(e) => setLiveAmount(e.target.value)} style={{ ...field, width: 120, marginTop: 4 }} />
                    </label>
                    <button type="button" disabled={busy} onClick={() => void doAddLive()} style={{ height: 36, marginTop: 16, padding: '0 16px', borderRadius: 8, border: 'none', background: '#c9a227', color: '#16120a', fontWeight: 800, cursor: busy ? 'default' : 'pointer' }}>{t.add}</button>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                        <button type="button" onClick={() => setTab('saldo')} style={{ height: 36, padding: '0 14px', borderRadius: 8, border: `1px solid ${dashLine}`, background: 'transparent', color: dashInk, cursor: 'pointer' }}>{t.tabSaldo}</button>
                        <button type="button" onClick={() => setTab('polog')} style={{ height: 36, padding: '0 14px', borderRadius: 8, border: `1px solid ${dashLine}`, background: 'transparent', color: dashInk, cursor: 'pointer' }}>{t.tabPolog}</button>
                        <button type="button" onClick={onBack} style={{ height: 36, padding: '0 14px', borderRadius: 8, border: `1px solid ${dashLine}`, background: 'transparent', color: dashDim, cursor: 'pointer' }}>{t.back}</button>
                    </div>
                </div>
                {msg ? <div style={{ padding: '10px 20px', color: '#e8c48a', fontSize: '0.85rem' }}>{msg}</div> : null}
                <div style={{ flex: 1, overflow: 'auto', padding: '8px 12px 24px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'clamp(1.35rem, 2.4vw, 2.1rem)' }}>
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left', padding: '18px 20px', fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: dashDim, borderBottom: `1px solid ${dashLine}`, position: 'sticky', top: 0, background: dashBg }}>{t.liveBranch}</th>
                                <th style={{ textAlign: 'right', padding: '18px 20px', fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: dashDim, borderBottom: `1px solid ${dashLine}`, position: 'sticky', top: 0, background: dashBg }}>{t.liveIn}</th>
                                <th style={{ textAlign: 'right', padding: '18px 20px', fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: dashDim, borderBottom: `1px solid ${dashLine}`, position: 'sticky', top: 0, background: dashBg }}>{t.liveOut}</th>
                                <th style={{ textAlign: 'right', padding: '18px 20px', fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: dashDim, borderBottom: `1px solid ${dashLine}`, position: 'sticky', top: 0, background: dashBg }}>{t.liveSaldo}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(live?.rows ?? []).length === 0 ? (
                                <tr><td colSpan={4} style={{ padding: 64, textAlign: 'center', color: dashDim, fontSize: '1.1rem' }}>{t.noData}</td></tr>
                            ) : (live?.rows ?? []).map((r) => (
                                <tr key={r.id}>
                                    <td style={{ padding: '20px 20px', borderBottom: `1px solid ${dashLine}`, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{r.branch_no}</td>
                                    <td style={{ padding: '20px 20px', borderBottom: `1px solid ${dashLine}`, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{eur(r.uplata)}</td>
                                    <td style={{ padding: '20px 20px', borderBottom: `1px solid ${dashLine}`, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.isplata < 0 ? '#e07070' : dashInk }}>{eur(r.isplata)}</td>
                                    <td style={{ padding: '20px 20px', borderBottom: `1px solid ${dashLine}`, textAlign: 'right', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{eur(r.saldo)}</td>
                                </tr>
                            ))}
                        </tbody>
                        {live && live.rows.length > 0 ? (
                            <tfoot>
                                <tr>
                                    <td style={{ padding: '22px 20px', fontWeight: 800 }}>{locale === 'hr' ? 'Ukupno' : 'Total'}</td>
                                    <td style={{ padding: '22px 20px', textAlign: 'right', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{eur(live.totals.uplata)}</td>
                                    <td style={{ padding: '22px 20px', textAlign: 'right', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{eur(live.totals.isplata)}</td>
                                    <td style={{ padding: '22px 20px', textAlign: 'right', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{eur(live.totals.saldo)}</td>
                                </tr>
                            </tfoot>
                        ) : null}
                    </table>
                </div>
            </div>
        );
    }

    return (
        <Page>
            <PageHeader icon="⚖️" title={t.title} subtitle={t.subtitle} actions={<Btn onClick={onBack}>{t.back}</Btn>} />

            <div style={{ display: 'flex', gap: 8 }}>
                <Btn variant="soft" onClick={() => setTab('dashboard')}>{t.openDash}</Btn>
                <Btn variant={tab === 'saldo' ? 'primary' : 'soft'} onClick={() => setTab('saldo')}>{t.tabSaldo}</Btn>
                <Btn variant={tab === 'polog' ? 'primary' : 'soft'} onClick={() => setTab('polog')}>{t.tabPolog}</Btn>
                <Btn variant={tab === 'sravnavanje' ? 'primary' : 'soft'} onClick={() => setTab('sravnavanje')}>{t.tabSrav}</Btn>
            </div>

            {msg ? <Card pad={12} style={{ color: ui.ink2, fontSize: '0.85rem' }}>{msg}</Card> : null}

            <Card pad={16}>
                <div style={{ fontWeight: 800, color: ui.ink, marginBottom: 8 }}>{t.ingest}</div>
                {fileRow(t.faros, farosRef, state?.faros ?? null, '.xlsx')}
                {fileRow(t.ismOpening, ismOpeningRef, state?.ism_opening ?? null, '.pdf')}
                {fileRow(t.ism, ismRef, state?.ism ?? null, '.xlsx,.pdf')}
                {fileRow(t.besoft, besoftRef, state?.besoft ?? null, '.xlsx')}
                <div style={{ marginTop: 12 }}>
                    <Btn variant="primary" disabled={busy} onClick={doIngest}>{t.upload}</Btn>
                </div>
            </Card>

            {tab === 'saldo' ? (
                <>
                    <Card pad={16}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <label style={{ fontSize: '0.8rem', color: ui.ink2, minWidth: 160 }}>{t.from}<br />
                                <DateInput value={dateFrom} onChange={setDateFrom} />
                            </label>
                            <label style={{ fontSize: '0.8rem', color: ui.ink2, minWidth: 160 }}>{t.to}<br />
                                <DateInput value={dateTo} onChange={setDateTo} />
                            </label>
                            <Btn variant="primary" disabled={busy} onClick={() => void loadSaldo(dateFrom, dateTo)}>{t.load}</Btn>
                            <Btn variant="soft" disabled={busy || !saldo} onClick={() => void doExportSaldo()}>{t.export}</Btn>
                        </div>
                        {saldo?.missing.includes('faros_dnevni_promet') ? (
                            <div style={{ marginTop: 10, fontSize: '0.8rem', color: ui.muted }}>{t.missing}</div>
                        ) : null}
                        {saldo ? (
                            <div style={{ marginTop: 10, fontSize: '0.82rem', color: ui.ink2 }}>
                                {locale === 'hr'
                                    ? `Početno ${eur(saldo.totals.opening_eur)} + uplate ${eur(saldo.totals.uplata_eur)} - isplate ${eur(saldo.totals.isplata_eur)} = završno ${eur(saldo.totals.closing_eur)}. Prijenos ${eur(saldo.totals.transfer_eur)}. Poslovnica: ${saldo.totals.branch_count}.`
                                    : `Opening ${eur(saldo.totals.opening_eur)} + deposits ${eur(saldo.totals.uplata_eur)} - withdrawals ${eur(saldo.totals.isplata_eur)} = closing ${eur(saldo.totals.closing_eur)}. Transfer ${eur(saldo.totals.transfer_eur)}. Branches: ${saldo.totals.branch_count}.`}
                            </div>
                        ) : null}
                    </Card>
                    <Card pad={4}>
                        <DataTable cols={saldoCols} rows={saldo?.rows ?? null} empty={t.noData} />
                    </Card>
                </>
            ) : tab === 'polog' ? (
                <>
                    <Card pad={16}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <label style={{ fontSize: '0.8rem', color: ui.ink2, minWidth: 160 }}>{t.date}<br />
                                <DateInput value={pologDate} onChange={setPologDate} />
                            </label>
                            <Btn variant="primary" disabled={busy} onClick={() => void loadPolog(pologDate)}>{t.load}</Btn>
                            <Btn variant="soft" disabled={busy || !polog} onClick={() => void doExportPolog()}>{t.export}</Btn>
                        </div>
                        {polog?.missing.includes('faros_dnevni_promet') ? (
                            <div style={{ marginTop: 10, fontSize: '0.8rem', color: ui.muted }}>{t.missing}</div>
                        ) : null}
                        {polog ? (
                            <div style={{ marginTop: 10, fontSize: '0.82rem', color: ui.ink2 }}>
                                {locale === 'hr'
                                    ? `Zlato-samo residual ${eur(polog.totals.gold_only_residual_eur)} EUR (ADMS cap ${eur(polog.legal.adms_company_eur)}; prekoračenje ${eur(polog.totals.gold_only_over_company_max)}). Poslovnica s pologom: ${polog.totals.polog_branch_count}.`
                                    : `Gold-only residual ${eur(polog.totals.gold_only_residual_eur)} EUR (ADMS cap ${eur(polog.legal.adms_company_eur)}; excess ${eur(polog.totals.gold_only_over_company_max)}). Branches needing deposit: ${polog.totals.polog_branch_count}.`}
                            </div>
                        ) : null}
                    </Card>
                    <Card pad={4}>
                        <DataTable cols={pologCols} rows={polog?.rows ?? null} empty={t.noData} />
                    </Card>
                </>
            ) : (
                <>
                    <Card pad={16}>
                        <div style={{ fontWeight: 800, color: ui.ink, marginBottom: 12 }}>{t.run}</div>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <label style={{ fontSize: '0.8rem', color: ui.ink2, minWidth: 160 }}>{t.from}<br /><DateInput value={dateFrom} onChange={setDateFrom} /></label>
                            <label style={{ fontSize: '0.8rem', color: ui.ink2, minWidth: 160 }}>{t.to}<br /><DateInput value={dateTo} onChange={setDateTo} /></label>
                            <label style={{ fontSize: '0.8rem', color: ui.ink2 }}>{t.type}<br /><Select value={runType} onChange={setRunType} width={150}><option value="full">full</option><option value="report_a">report_a</option><option value="report_b">report_b</option><option value="report_c">report_c</option><option value="transfer">transfer</option></Select></label>
                            <label style={{ fontSize: '0.8rem', color: ui.ink2 }}>{t.tolerance}<br /><input type="number" step="0.01" value={tolerance} onChange={(e) => setTolerance(e.target.value)} style={{ height: 38, width: 100, padding: '0 10px', borderRadius: ui.radiusSm, border: `1px solid ${ui.line}` }} /></label>
                            <Btn variant="primary" disabled={busy} onClick={doRun}>{t.start}</Btn>
                        </div>
                    </Card>
                    <Card pad={4}>
                        <div style={{ fontWeight: 800, color: ui.ink, padding: '12px 14px 0' }}>{t.runs}</div>
                        <DataTable cols={runCols} rows={runs} onRowClick={(r) => openRun(r.id)} empty={t.noData} />
                    </Card>
                </>
            )}
        </Page>
    );
}
